import React, { ChangeEvent, FormEvent, useContext, useMemo, useState } from 'react'
import TextInput from '../../../../components/text-input'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import {
  decodeQuery,
  doReportsSearch,
  getInvalidReportSearchQueryMessage,
  getVerdictLabel,
  uniqueVerdicts,
} from 'app/modules/reports/helpers'
import { applicationConfiguration, MainNotificationContext, useURLSearchParams } from 'app/modules/shared'
import { SearchParams, SignalVerdict, threatVerdicts } from 'app/modules/reports/types'
import Checkbox from '../../../../components/checkbox'
import { Form } from 'react-bootstrap'
import Button from 'app/modules/shared/components/button'
import { useNavigate } from 'react-router'
import DatePicker from '../../../../components/datepicker/datepicker'
import { PickerDate, Range } from 'app/components/datepicker/types'
import { formatSearchDate, getUpdatedVerdictList } from 'app/modules/reports/features/search/threat-hunting'
import YaraSearch from 'app/modules/reports/features/search/full-page-search-forms/yara-rules-search'
import MitreSearch from 'app/modules/reports/features/search/full-page-search-forms/mitre-search'
import ThreatIndicatorsSearch from './full-page-search-forms/threat-indicators-search'
import { AuthContext } from 'app/modules/user'
import { getPageUrl } from 'app/modules/shared/routing'

const ThreatHunting = () => {
  const { _t } = useTypedTranslation()
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const searchParams = useURLSearchParams()
  const { useHumanFriendlyVerdicts } = useContext(applicationConfiguration)
  const mainNotification = useContext(MainNotificationContext)
  const params = useMemo(() => {
    const result: { [key: string]: string } = {}
    searchParams.forEach((value, key) => {
      result[key] = value
    })

    return decodeQuery(result)
  }, [searchParams])

  const [filters, setFilters] = useState<SearchParams>({
    query: params.query ?? '',
    verdict: params.verdict ?? threatVerdicts.join(','),
    yara_rule: params.yara_rule ?? '',
    date_from: params.date_from ?? '',
    date_to: params.date_to ?? '',
    domain: params.domain ?? '',
    ip: params.ip ?? '',
    url: params.url ?? '',
    sha1: params.sha1 ?? '',
    sha256: params.sha256 ?? '',
    sha512: params.sha512 ?? '',
    md5: params.md5 ?? '',
    signal_groups: params.signal_groups ?? '',
    mitre_techniques: params.mitre_techniques ?? '',
  })

  const handleVerdictFilterChange = (verdict: SignalVerdict, checked: boolean) => {
    setFilters({ ...filters, verdict: getUpdatedVerdictList(filters.verdict, verdict) })
  }

  const handleDateFilterChange = (dateRange: Range<PickerDate>) => {
    setFilters({
      ...filters,
      date_from: formatSearchDate(dateRange[0]),
      date_to: formatSearchDate(dateRange[1]),
    })
  }

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id: key, value } = e.currentTarget as HTMLInputElement

    setFilters({
      ...filters,
      [key]: value,
    })
  }

  const handleFullPageFilterChange = (key: string, value: string[]) =>
    setFilters({ ...filters, [key]: value.join(',') })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const validationError = getInvalidReportSearchQueryMessage(filters.query)

    if (validationError) {
      mainNotification.setMessageData(validationError, 'danger')
      return
    }

    doReportsSearch(filters, navigate)
  }

  if (!user) {
    return (
      <div className="signin-required-view">
        <img
          src="/assets/imgs/pages/hunting-signin-required.png"
          alt="Sign up required"
          style={{ width: '120px', height: '120px' }}
          className="mb-2"
        />

        <p className="m-0 h2">You need to sign in to have access to this feature</p>

        <Button
          id="home-threat-hunting-sign-in"
          variant="primary"
          onClick={() => navigate(getPageUrl('sign-in') as string)}
        >
          {_t('sign-in')}
        </Button>
      </div>
    )
  }

  return (
    <Form onSubmit={handleSubmit} className="threat-hunting-container">
      <div className="d-block">
        <p className="text-note text-m m-0 pb-1">{_t('search')}</p>
        <TextInput
          id="query"
          className="align-self-center"
          placeholder={_t('search-bar-placeholder')}
          value={filters.query}
          onChange={handleFilterChange}
          prefix={<i className="ds3-icon ds3-search" />}
          // postfix={<i className="ds3 ds3-help" />} // Disabled helper until having helper text
          clearable
          size="big"
        />
      </div>
      <div className="search-inputs-container">
        <div className="search-inputs-verdict-homepage">
          <p className="text-note text-m m-0 pb-1">{_t('verdict')}</p>
          <div className="verdict-checkbox-container">
            {Object.values(uniqueVerdicts())
              .reverse()
              .map((verdict) => (
                <Checkbox
                  key={verdict}
                  onChange={(checked) => handleVerdictFilterChange(verdict, checked)}
                  checked={filters.verdict?.split(',').includes(verdict.toLowerCase())}
                  id={`verdict-${verdict}`}
                  label={getVerdictLabel(verdict, useHumanFriendlyVerdicts)}
                  variant={verdict}
                />
              ))}
          </div>
        </div>
        <div className="search-inputs-date-homepage">
          <p className="text-note text-m m-0 pb-1">{_t('date')}</p>
          <DatePicker handleDateChange={handleDateFilterChange} />
        </div>
      </div>
      <div className="search-inputs-container">
        <div className="search-input-block">
          <YaraSearch
            onApply={(rules: string[]) => handleFullPageFilterChange('yara_rule', rules)}
            searchRules={filters.yara_rule ? filters.yara_rule.split(',') : []}
            filterId="yara_rule"
            value={filters.yara_rule}
          />
        </div>
        <div className="search-input-block">
          <ThreatIndicatorsSearch
            onApply={(rules: string[]) => handleFullPageFilterChange('signal_groups', rules)}
            searchIndicators={filters.signal_groups ? filters.signal_groups.split(',') : []}
            filterId="signal_groups"
            value={filters.signal_groups}
          />
        </div>
        <div className="search-input-block">
          <MitreSearch
            onApply={(techniques: string[]) => handleFullPageFilterChange('mitre_techniques', techniques)}
            searchTechniques={filters.mitre_techniques ? filters.mitre_techniques.split(',') : []}
            filterId="mitre_techniques"
            value={filters.mitre_techniques}
          />
        </div>
      </div>
      <div className="threat-hunting-homepage-button-group">
        <Button id="home-threat-hunting-more-options" variant="secondary" onClick={() => navigate('/advanced-search')}>
          {_t('more-options')}
        </Button>
        <Button id="home-threat-hunting-search" type="submit" variant="primary">
          {_t('search')}
        </Button>
      </div>
    </Form>
  )
}

export default ThreatHunting
